import { Button, H4, Intent, Tooltip } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import LoadingFullScreen from "components/common/LoadingFullScreen";
import { useUserDataContext } from "contexts/UserDataContext/UserDataContext";
import { IN_QUEUE_PATH } from "infrastructure/RouterApp";
import { useState } from "react";
import {
    Navigate,
    useNavigate,
    useParams,
    useSearchParams
} from "react-router-dom";
import { ADMIN_TYPE } from "types/roleType";
import { BackButton } from "./components/common/BackButton";
import { SectionHeader } from "./components/common/SectionHeader";
import { PortfolioEditAlert } from "./components/ScenarioBuilder/PortfolioEditAlert";
import {
    DUPLICATE_FROM_PARAM,
    ScenarioBuilderLabel
} from "./components/ScenarioBuilder/scenarioBuilderParams";
import { ScenarioStepTimeline } from "./components/ScenarioBuilder/ScenarioStepTimeline";
import { SensitivitiesStep } from "./components/ScenarioBuilder/SensitivitiesStep";
import { SubmitScenarioFields } from "./components/ScenarioBuilder/SubmitScenarioFields";
import { useRequestedScenarioStage } from "./components/ScenarioBuilder/useRequestedScenarioStage";
import {
    ScenarioBuilderAction,
    useIsScenarioBuilderDataReady,
    useScenarioBuilderState
} from "./components/ScenarioBuilder/useScenarioBuilderState";
import { AddUpgradesPanel } from "./components/ScenarioDialog/AddUpgradesPanel";
import { CostAssumptionsPanel } from "./components/ScenarioDialog/CostAssumptionsPanel";
import { useCluster, useStageConfig } from "./contexts/ClusterContext";
import {
    useScenarioMetadata,
    useTrackedProject
} from "./contexts/ProjectDataContext";
import { useCustomUpgradesStatus } from "./hooks/useCustomUpgradesStatus";
import { useIsScenarioCreationEnabled } from "./hooks/useIsScenarioCreationEnabled";
import css from "./ScenarioBuilderPage.module.scss";
import {
    ScenarioMetadata,
    ScenarioStage,
    ScenarioStatus
} from "./types/scenarioType";

interface BuilderStep {
    readonly title: string;
    readonly panel: JSX.Element;
}

export const ScenarioBuilderPage: React.FC = () => {
    const { projectId, scenarioId } = useParams();
    const [searchParams] = useSearchParams();
    const cluster = useCluster();
    const { role } = useUserDataContext();
    const isDataReady = useIsScenarioBuilderDataReady();

    const project = useTrackedProject({
        projectId,
        phase: cluster.studyPhase
    });

    const action = scenarioId !== undefined ? "update" : "create";
    const seedScenarioId =
        scenarioId ?? searchParams.get(DUPLICATE_FROM_PARAM) ?? undefined;
    const seedScenario = useScenarioMetadata(projectId, seedScenarioId);
    const seed =
        seedScenario === "loading" || seedScenario === undefined
            ? undefined
            : seedScenario;

    const requestedStage = useRequestedScenarioStage();
    const scenarioStage: ScenarioStage = seed?.stage ?? requestedStage;

    const scenarioCreationEnabled = useIsScenarioCreationEnabled({
        stage: scenarioStage
    });

    const isAllowed =
        action === "update" ? role === ADMIN_TYPE : scenarioCreationEnabled;

    const projectHref = `/${IN_QUEUE_PATH}/${cluster.slug}/${projectId}`;

    if (project === "loading" || seedScenario === "loading") {
        return <LoadingFullScreen />;
    }
    if (projectId === undefined || project === undefined) {
        // Project doesn't exist, redirect to portfolio
        return <Navigate to={`/${IN_QUEUE_PATH}`} replace />;
    }

    if (seedScenarioId !== undefined && seedScenario === undefined) {
        // Project exists but scenario doesn't, redirect to project page
        return <Navigate to={projectHref} replace />;
    }
    if (!isDataReady) {
        return <LoadingFullScreen />;
    }

    if (!isAllowed) {
        return <Navigate to={projectHref} replace />;
    }

    if (
        action === "update" &&
        seed !== undefined &&
        (seed.status !== ScenarioStatus.Draft || seed.archived)
    ) {
        return <Navigate to={projectHref} replace />;
    }

    return (
        <ScenarioBuilderForm
            key={`${action}-${seed?.scenarioId ?? "new"}-${scenarioStage}`}
            action={action}
            seed={seed}
            scenarioStage={scenarioStage}
            projectId={projectId}
            projectHref={projectHref}
        />
    );
};

interface ScenarioBuilderFormProps {
    readonly action: ScenarioBuilderAction;
    readonly seed: ScenarioMetadata | undefined;
    readonly scenarioStage: ScenarioStage;
    readonly projectId: string;
    readonly projectHref: string;
}

const ScenarioBuilderForm: React.FC<ScenarioBuilderFormProps> = ({
    action,
    seed,
    scenarioStage,
    projectId,
    projectHref
}: ScenarioBuilderFormProps) => {
    const navigate = useNavigate();
    const cluster = useCluster();
    const stageConfig = useStageConfig();
    const upgradesStatus = useCustomUpgradesStatus();
    const [activeIndex, setActiveIndex] = useState(0);
    const [pendingStep, setPendingStep] = useState<number>();
    // Dismissed for the builder session, so it must survive step changes.
    const [isErisWarningDismissed, setIsErisWarningDismissed] = useState(false);
    const builder = useScenarioBuilderState({
        action,
        scenario: seed,
        scenarioStage
    });

    const finalStepTitle =
        action === "update"
            ? ScenarioBuilderLabel.EditScenario
            : ScenarioBuilderLabel.SubmitScenario;

    const steps: BuilderStep[] = [
        {
            title: ScenarioBuilderLabel.Sensitivities,
            panel: (
                <SensitivitiesStep
                    projectSizeAssumptions={builder.projectSizeAssumptions}
                    setProjectSizeAssumptions={
                        builder.setProjectSizeAssumptions
                    }
                    includedSpeculativeProjectIds={
                        builder.includedSpeculativeProjectIds
                    }
                    setIncludedSpeculativeProjectIds={
                        builder.setIncludedSpeculativeProjectIds
                    }
                    isErisWarningDismissed={isErisWarningDismissed}
                    setIsErisWarningDismissed={setIsErisWarningDismissed}
                />
            )
        }
    ];

    if (upgradesStatus !== "hidden") {
        steps.push({
            title: ScenarioBuilderLabel.AddUpgrades,
            panel: (
                <AddUpgradesPanel
                    selectedUpgradeId={builder.selectedUpgradeId}
                    setSelectedUpgradeId={builder.setSelectedUpgradeId}
                />
            )
        });
    }

    if (stageConfig.supportedCostAssumptionTypes[scenarioStage].length > 0) {
        steps.push({
            title: ScenarioBuilderLabel.CostAssumptions,
            panel: (
                <CostAssumptionsPanel
                    costAssumptions={builder.costAssumptions}
                    setCostAssumptions={builder.setCostAssumptions}
                    scenarioStage={scenarioStage}
                />
            )
        });
    }

    steps.push({
        title: finalStepTitle,
        panel: (
            <>
                <H4>{finalStepTitle}</H4>
                <SubmitScenarioFields action={action} builder={builder} />
            </>
        )
    });

    const stepIndex = Math.min(activeIndex, steps.length - 1);
    // Leaving the sensitivities step is the one moment to warn about edited
    // portfolio projects, whether by Next or the timeline.
    const goToStep = (index: number) =>
        stepIndex === 0 &&
        index > 0 &&
        builder.editedPortfolioProjectIds.length > 0
            ? setPendingStep(index)
            : setActiveIndex(index);
    const activeStep = steps[stepIndex];
    const isFinalStep = stepIndex === steps.length - 1;

    const onSubmit = async () => {
        const result = await builder.submit();
        if (result.outcome === "success") {
            const ownerProjectId =
                builder.parentProject?.projectId ?? projectId;
            navigate(
                `/${IN_QUEUE_PATH}/${cluster.slug}/${ownerProjectId}/scenario/${result.scenarioId}`
            );
        }
    };

    return (
        <>
            <BackButton
                text={`Back to Project ${projectId}`}
                to={projectHref}
            />
            <div className={css["ScenarioBuilderPage"]}>
                <SectionHeader
                    level="h1"
                    title={builder.title || ScenarioBuilderLabel.NewScenario}
                />
                <ScenarioStepTimeline
                    steps={steps.map((step) => step.title)}
                    activeIndex={stepIndex}
                    onSelectStep={goToStep}
                />
                <div className={css["ScenarioBuilderPage-panel"]}>
                    {activeStep.panel}
                </div>
                <div className={css["ScenarioBuilderPage-footer"]}>
                    <Button
                        className={css["ScenarioBuilderPage-footer-close"]}
                        text="Close"
                        minimal
                        onClick={() => navigate(projectHref)}
                    />
                    <Button
                        text="Back"
                        disabled={stepIndex === 0}
                        onClick={() => setActiveIndex(stepIndex - 1)}
                    />
                    {isFinalStep ? (
                        <Tooltip
                            content={builder.submitTooltip}
                            disabled={builder.submitTooltip === undefined}
                        >
                            <Button
                                text={finalStepTitle}
                                intent={Intent.PRIMARY}
                                icon={IconNames.ADD}
                                disabled={builder.disableSubmit}
                                loading={builder.isLoading}
                                onClick={onSubmit}
                            />
                        </Tooltip>
                    ) : (
                        <Button
                            text="Next"
                            intent={Intent.PRIMARY}
                            onClick={() => goToStep(stepIndex + 1)}
                        />
                    )}
                </div>
            </div>
            {pendingStep !== undefined && (
                <PortfolioEditAlert
                    projectIds={builder.editedPortfolioProjectIds}
                    onCancel={() => setPendingStep(undefined)}
                    onConfirm={() => {
                        setActiveIndex(pendingStep);
                        setPendingStep(undefined);
                    }}
                />
            )}
        </>
    );
};
